(ns app.front.ui.v2.composed-card
  (:require ["@carbon/icons-react/es/chevron--down/24" :default ChevronDown24]
            [app.front.ui.status :as ui-status]
            [app.front.ui.v2.status-tag :as ui-status-tag]))


;; ATOMS - stateless visual building blocks
;; If pre-formatted card component does not work for the use case, use these to build your own

(defn expand-toggle
  [{:keys [expanded?]}]
  [:span.flex.flex-row.items-center
   {:class ["transition-transform duration-200 ease-out"
            (if expanded? "-rotate-180" "rotate-0")]}
   [:> ChevronDown24]])

(defn section
  "Padded section of a card
  `class` optional extra classes for the section"
  [{:keys [class]} & children]
  (into [:div.p-4.pl-5 {:class class}] children))

(defn title
  "Card title styling"
  [_ & children]
  (into [:div.font-semibold.text-sm] children))

(defn avatar
  "Icon or image wrapped in a rounded border
  `icon` React component containing the icon
  `image` string for image src attribute"
  [{:keys [icon image]}]
  [:div.rounded-md.border.border-solid.border-gray-400.flex.items-center.justify-center.w-12.h-12.overflow-hidden
   (when icon [:> icon])
   (when image [:img.self-stretch.justify-self-stretch.object-cover {:src image}])])

(defn slim-button
  "Text button for tight spaces, ideal for card header call-to-action"
  [{:keys [icon on-click]} & children]
  [:button.inline-flex.flex-row.self-auto.h-fit.items-center.px-2.py-1.text-xs.bg-transparent.border-none.uppercase.gap-2
   {:class    "text-ui-night-sky-100 leading-none hover:bg-ui-violet-100 hover:text-ui-white focus:bg-ui-violet-20"
    :on-click on-click}
   (into [:<>] children)
   (when icon
     [:div.-my-1.flex.items-center.justify-center
      [:> icon]])])

(defn row
  "Row of elements inside a card section, with a nice spacing by default
  `align` keyword to define how content is aligned, one of :left :right :center :stretch :between
  `spacing?` boolean, defaults to true, removes gaps when explicitly set to false"
  [{:keys [align spacing?]} & children]
  (into [:div.flex.flex-row.flex-wrap
         {:data-card-row true
          :class         ["[[data-card-row]+&]:mt-3"
                          (case align
                            :left "justify-start"
                            :right "justify-end"
                            :center "justify-center"
                            :stretch "justify-stretch"
                            :between "justify-between"
                            "justify-normal")
                          (when-not (false? spacing?) "gap-2")]}]
        children))

(defn label
  "Data label styling"
  [_ & children]
  (into [:div.font-medium] children))

(defn labelled-row
  "Data row with a label on the left side and value on the right side"
  [{label-text :label} & children]
  [row {:align :between}
   [label {} label-text]
   (into [:div.font-light] children)])

(defn status-indicator
  "Colored stripe to be used as a status indicator, should be a direct child of wrapper"
  [status]
  [:div.absolute.left-0.top-0.h-full.border-0
   {:class ["w-[5px] rounded-l-[3px]"
            (ui-status/status->bg-color-class status)]}])

(defn wrapper
  "Root level wrapper for a single card
  `class` classes to be added to the wrapper div
  `dividers?` boolean to show divider borders between sections, defaults to true unless placeholder? true
  `placeholder?` boolean to enable placeholder-like styling (dashed borders etc.), defaults to false
  `status` keyword to show colored status indicator stripe on left border of the wrapper (not shown when placeholder?)"
  [{:keys [class dividers? placeholder? status]} & children]
  [:div.relative.h-full.rounded.border.bg-ui-white.border-gray-400
   {:class [(if placeholder? "border-dashed" "border-solid") class]}
   ;; Full-height flex column: cards stretch to equal heights in grids, footers pin to the bottom.
   (into [:div.flex.h-full.flex-col
          {:class [(when-not (or placeholder? (false? dividers?))
                     "divide-y divide-gray-400")
                   (if placeholder? "divide-dashed" "divide-solid")]}]
         children)
   (when (and status
              (not placeholder?))
     [status-indicator status])])

;; PRE-FORMATTED CARDS

(defn card
  "Generic card component, use this pre-formatted or build your own from the atoms given above
  `title` string, title of the card displayed in header
  `avatar-data` map, either {:icon IconElement} or {:image src}
  `dividers?` boolean to show divider borders between sections, enabled by default unless placeholder? true
  `placeholder?` boolean to enable placeholder-like styling (dashed borders etc.), defaults to false
  `status` keyword to show colored status indicator stripe on left border of the wrapper (not shown when placeholder?)
  `status-text` string, shown in a tag in card header, tag color defined by `status` parameter
  `content-class` string, extra classes for the content section (e.g. to center placeholder content)"
  [{title-content :title
    avatar-data   :avatar
    :keys         [dividers? placeholder? status status-text header-content footer-content content-class]}
   & children]
  [wrapper {:dividers?    dividers?
            :placeholder? placeholder?
            :status       status}
   ;; Header section (show only when needed):
   (when (or avatar-data status-text header-content title-content)
     [section {}
      [:div.flex.flex-row.gap-4
       (when avatar-data
         [avatar avatar-data])
       [:div.flex.flex-col.justify-between.flex-1
        (when (or status-text header-content)
          [row {:align :between}
           (if status-text
             [ui-status-tag/tag {:status status} status-text]
             [:div.flex-1])
           (when header-content
             [:div header-content])])
        (when title-content
          [row {}
           [title {} title-content]])]]])
   ;; Content section:
   (into [section {:class ["grow" content-class]}] children)
   ;; Footer section:
   (when footer-content
     [section {} footer-content])])
